import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTranslation, useLanguage } from '../context/LanguageContext';
import { useUnits } from '../context/UnitsContext';
import { useAuth } from '../context/AuthContext';
import { createPlan, logWeight } from '../lib/api';
import { API_URL } from '../config/api';
import { Scale, ChevronLeft, ChevronRight, Target, Info, TrendingDown, User } from 'lucide-react';
import GeoSettingsScreen from './GeoSettingsScreen';

// Weight Drum Picker Component
function WeightDrumPicker({ value, onChange, isDark, unit = 'kg' }) {
  // For kg: 20-200, for lbs: 44-440
  const isLbs = unit === 'lbs';
  const minVal = isLbs ? 44 : 20;
  const maxVal = isLbs ? 440 : 200;
  const range = useMemo(() => Array.from({ length: maxVal - minVal + 1 }, (_, i) => i + minVal), [minVal, maxVal]);
  const decimalRange = useMemo(() => Array.from({ length: 10 }, (_, i) => i), []);
  
  // Convert value to display unit
  const displayValue = value || 90; // Default 90 (assumes kg input)
  const convertedValue = isLbs ? displayValue * 2.20462 : displayValue;
  const currentMain = Math.floor(convertedValue);
  const currentDecimal = Math.round((convertedValue - Math.floor(convertedValue)) * 10);
  
  const mainRef = useRef(null);
  const decimalRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize scroll position only once on mount
  useEffect(() => {
    const initValue = value || 90;
    const converted = isLbs ? initValue * 2.20462 : initValue;
    const main = Math.floor(converted);
    const decimal = Math.round((converted - Math.floor(converted)) * 10);
    
    // Immediate scroll without animation
    if (mainRef.current) {
      mainRef.current.style.scrollBehavior = 'auto';
      const index = range.indexOf(main);
      if (index !== -1) {
        mainRef.current.scrollTop = index * 44;
      }
      // Re-enable smooth after initialization
      setTimeout(() => {
        if (mainRef.current) mainRef.current.style.scrollBehavior = 'smooth';
      }, 100);
    }
    if (decimalRef.current) {
      decimalRef.current.style.scrollBehavior = 'auto';
      decimalRef.current.scrollTop = decimal * 44;
      setTimeout(() => {
        if (decimalRef.current) decimalRef.current.style.scrollBehavior = 'smooth';
      }, 100);
    }
    // Enable scroll handlers after initialization
    setTimeout(() => setIsInitialized(true), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMainScroll = (e) => {
    if (!isInitialized) return;
    const scrollTop = e.target.scrollTop;
    const itemHeight = 44;
    const index = Math.round(scrollTop / itemHeight);
    if (range[index] !== undefined) {
      const displayVal = range[index] + (currentDecimal / 10);
      // Convert back to kg for storage
      const kgValue = isLbs ? displayVal / 2.20462 : displayVal;
      onChange(kgValue);
    }
  };

  const handleDecimalScroll = (e) => {
    if (!isInitialized) return;
    const scrollTop = e.target.scrollTop;
    const itemHeight = 44;
    const index = Math.round(scrollTop / itemHeight);
    if (decimalRange[index] !== undefined) {
      const displayVal = currentMain + (decimalRange[index] / 10);
      // Convert back to kg for storage
      const kgValue = isLbs ? displayVal / 2.20462 : displayVal;
      onChange(kgValue);
    }
  };

  return (
    <div>
      {/* Weight Display */}
      <div className="text-center mb-2">
        <span className="font-display text-5xl font-bold text-[#1d3557]">
          {currentMain}.{currentDecimal}
        </span>
        <span className={`text-xl ${isDark ? 'text-white/50' : 'text-gray-500'}`}> {unit}</span>
      </div>

      {/* Drum Labels */}
      <div className="flex justify-center gap-16 text-xs font-medium uppercase tracking-wider mb-2">
        <span className={isDark ? 'text-white/50' : 'text-gray-500'}>{isLbs ? 'LBS' : 'KG'}</span>
        <span className={isDark ? 'text-white/50' : 'text-gray-500'}>.{isLbs ? 'LBS' : 'G'}</span>
      </div>

      {/* Drum Picker Container */}
      <div className={`flex items-center justify-center rounded-xl overflow-hidden h-36 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
        {/* Main Column */}
        <div className="relative h-full w-28 overflow-hidden">
          <div 
            ref={mainRef}
            className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
            style={{ scrollBehavior: 'smooth' }}
            onScroll={handleMainScroll}
          >
            <div className="h-[44px]"></div>
            {range.map(val => (
              <div 
                key={val}
                className={`h-11 flex items-center justify-center snap-center font-display transition-all ${
                  currentMain === val 
                    ? 'text-[#1d3557] font-bold text-3xl'
                    : Math.abs(currentMain - val) === 1
                      ? isDark ? 'text-white/40 text-xl' : 'text-gray-400 text-xl'
                      : isDark ? 'text-white/20 text-lg' : 'text-gray-300 text-lg'
                }`}
              >
                {val}
              </div>
            ))}
            <div className="h-[44px]"></div>
          </div>
          <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 h-11 border-y pointer-events-none" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}></div>
        </div>

        {/* Separator */}
        <span className={`font-display text-3xl font-bold ${isDark ? 'text-white/30' : 'text-gray-300'}`}>.</span>

        {/* Decimals Column */}
        <div className="relative h-full w-20 overflow-hidden">
          <div 
            ref={decimalRef}
            className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
            style={{ scrollBehavior: 'smooth' }}
            onScroll={handleDecimalScroll}
          >
            <div className="h-[44px]"></div>
            {decimalRange.map(d => (
              <div 
                key={d}
                className={`h-11 flex items-center justify-center snap-center font-display transition-all ${
                  currentDecimal === d 
                    ? 'text-[#1d3557] font-bold text-3xl'
                    : Math.abs(currentDecimal - d) === 1
                      ? isDark ? 'text-white/40 text-xl' : 'text-gray-400 text-xl'
                      : isDark ? 'text-white/20 text-lg' : 'text-gray-300 text-lg'
                }`}
              >
                {d}
              </div>
            ))}
            <div className="h-[44px]"></div>
          </div>
          <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 h-11 border-y pointer-events-none" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}></div>
        </div>
      </div>
    </div>
  );
}

// Height Picker Component
function HeightPicker({ value, onChange, isDark, unit = 'cm' }) {
  const isFt = unit === 'ft';
  
  if (isFt) {
    // Feet and inches picker
    const feetRange = Array.from({ length: 5 }, (_, i) => i + 4); // 4-8 feet
    const inchesRange = Array.from({ length: 12 }, (_, i) => i); // 0-11 inches
    
    // Convert cm to ft/in for display
    const totalInches = (value || 170) * 0.393701;
    const currentFeet = Math.floor(totalInches / 12);
    const currentInches = Math.round(totalInches % 12);
    
    const handleFeetChange = (feet) => {
      const newTotalInches = feet * 12 + currentInches;
      const newCm = Math.round(newTotalInches / 0.393701);
      onChange(newCm);
    };
    
    const handleInchesChange = (inches) => {
      const newTotalInches = currentFeet * 12 + inches;
      const newCm = Math.round(newTotalInches / 0.393701);
      onChange(newCm);
    };
    
    return (
      <div>
        <div className="text-center mb-4">
          <span className="font-display text-5xl font-bold text-[#1d3557]">
            {currentFeet}'{currentInches}"
          </span>
        </div>
        
        <div className="flex justify-center gap-4">
          <div className="flex flex-col items-center">
            <span className={`text-xs mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>FEET</span>
            <div className="flex gap-2">
              {feetRange.map(ft => (
                <button
                  key={ft}
                  onClick={() => handleFeetChange(ft)}
                  className={`w-12 h-12 rounded-xl font-display text-lg transition-all ${
                    currentFeet === ft
                      ? 'bg-[#1d3557] text-white'
                      : isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {ft}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="mt-4">
          <span className={`text-xs block text-center mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>INCHES</span>
          <div className="flex flex-wrap justify-center gap-2">
            {inchesRange.map(inch => (
              <button
                key={inch}
                onClick={() => handleInchesChange(inch)}
                className={`w-10 h-10 rounded-xl font-display transition-all ${
                  currentInches === inch
                    ? 'bg-[#1d3557] text-white'
                    : isDark ? 'bg-white/10 text-white/70' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {inch}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // CM picker (slider style)
  const cmRange = Array.from({ length: 81 }, (_, i) => i + 140); // 140-220 cm
  const currentCm = value || 170;
  
  return (
    <div>
      <div className="text-center mb-4">
        <span className="font-display text-5xl font-bold text-[#1d3557]">
          {currentCm}
        </span>
        <span className={`text-xl ${isDark ? 'text-white/50' : 'text-gray-500'}`}> cm</span>
      </div>
      
      <input
        type="range"
        min="140"
        max="220"
        value={currentCm}
        onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#1d3557]"
      />
      
      <div className="flex justify-between text-xs mt-2">
        <span className={isDark ? 'text-white/50' : 'text-gray-500'}>140 cm</span>
        <span className={isDark ? 'text-white/50' : 'text-gray-500'}>220 cm</span>
      </div>
    </div>
  );
}

// Age Picker Component - Scrollable wheel like weight picker
function AgePicker({ value, onChange, isDark }) {
  // Age range: 12-99
  const ageRange = useMemo(() => Array.from({ length: 88 }, (_, i) => i + 12), []);
  const currentAge = value || 30;
  
  const scrollRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize scroll position only once on mount
  useEffect(() => {
    const age = value || 30;
    
    if (scrollRef.current) {
      scrollRef.current.style.scrollBehavior = 'auto';
      const index = ageRange.indexOf(age);
      if (index !== -1) {
        scrollRef.current.scrollTop = index * 44;
      }
      
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.style.scrollBehavior = 'smooth';
        }
        setIsInitialized(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount
  
  const handleScroll = (e) => {
    if (!isInitialized) return;
    const scrollTop = e.target.scrollTop;
    const itemHeight = 44;
    const index = Math.round(scrollTop / itemHeight);
    if (ageRange[index] !== undefined && ageRange[index] !== currentAge) {
      onChange(ageRange[index]);
    }
  };

  // Prevent page scroll when touching the picker
  const handleTouchStart = (e) => {
    e.stopPropagation();
  };

  return (
    <div 
      onTouchStart={handleTouchStart}
      onTouchMove={(e) => e.stopPropagation()}
      style={{ touchAction: 'none' }}
    >
      {/* Age Display */}
      <div className="text-center mb-4">
        <span className="font-display text-5xl font-bold text-[#F59E0B]">
          {currentAge}
        </span>
        <span className={`text-xl ml-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>anni</span>
      </div>

      {/* Drum Picker */}
      <div className={`flex items-center justify-center rounded-xl overflow-hidden h-36 ${isDark ? 'bg-white/5' : 'bg-gray-100'}`}>
        <div className="relative h-full w-32 overflow-hidden">
          <div 
            ref={scrollRef}
            className="h-full overflow-y-scroll no-scrollbar snap-y snap-mandatory"
            style={{ 
              scrollBehavior: 'smooth',
              overscrollBehavior: 'contain',
              touchAction: 'pan-y'
            }}
            onScroll={handleScroll}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
          >
            <div className="h-[44px]"></div>
            {ageRange.map(age => (
              <div 
                key={age}
                className={`h-11 flex items-center justify-center snap-center font-display transition-all ${
                  currentAge === age 
                    ? 'text-[#F59E0B] font-bold text-3xl'
                    : Math.abs(currentAge - age) === 1
                      ? isDark ? 'text-white/40 text-xl' : 'text-gray-400 text-xl'
                      : Math.abs(currentAge - age) === 2
                        ? isDark ? 'text-white/25 text-lg' : 'text-gray-300 text-lg'
                        : isDark ? 'text-white/15 text-base' : 'text-gray-200 text-base'
                }`}
              >
                {age}
              </div>
            ))}
            <div className="h-[44px]"></div>
          </div>
          <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 h-11 border-y pointer-events-none" style={{ borderColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }}></div>
        </div>
      </div>
    </div>
  );
}

export default function NuovoPiano() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { geoData, language } = useLanguage();
  const { user, refreshUser, loading: authLoading } = useAuth();
  const { weightUnit, heightUnit, convertWeightToKg, convertWeight } = useUnits();
  
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showGeoSettings, setShowGeoSettings] = useState(null); // null = checking, true = show, false = skip
  const TOTAL_STEPS = 8;
  
  // Initialize with user data if available
  // Always store weights in kg internally
  const [formData, setFormData] = useState({
    name: 'Il mio percorso',
    display_name: user?.name || '',
    nickname: user?.username || '',
    height_cm: user?.height_cm || 170,
    sex: user?.sex || null,
    age: user?.age || 30,
    activity_level: user?.activity_level || 'moderate',
    start_weight: 90, // Always 90kg default
    target_weight: 80, // Always 80kg default (10kg less)
    weekly_goal: 0.5,
    duration_weeks: 12
  });
  
  // Check if user needs to configure settings first
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }
    
    // If user has weight_unit saved in DB, skip GeoSettingsScreen
    // This is the only check - we don't use localStorage anymore
    if (user?.weight_unit) {
      setShowGeoSettings(false);
      return;
    }
    
    // For new users without settings, show the geo settings screen
    setShowGeoSettings(true);
  }, [user, authLoading]);
  
  // Handle completion of geo settings
  const handleGeoSettingsComplete = () => {
    setShowGeoSettings(false);
    // Refresh user to get updated settings
    if (refreshUser) {
      refreshUser();
    }
  };
  
  // Show loading while checking
  if (showGeoSettings === null) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`}>
        <div className="w-12 h-12 border-4 border-[#E8334A] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  // Show GeoSettingsScreen first if needed
  if (showGeoSettings) {
    return <GeoSettingsScreen onComplete={handleGeoSettingsComplete} />;
  }

  const handleNext = () => {
    setError('');
    
    // Validation per step
    if (step === 1 && !formData.display_name?.trim()) {
      setError(t('wizard.enterName') || 'Inserisci il tuo nome');
      return;
    }
    if (step === 2 && !formData.height_cm) {
      setError(t('wizard.yourHeight'));
      return;
    }
    if (step === 3 && !formData.sex) {
      setError(t('wizard.yourSex'));
      return;
    }
    if (step === 4 && !formData.age) {
      setError(t('wizard.yourAge'));
      return;
    }
    if (step === 6 && !formData.start_weight) {
      setError(t('wizard.currentWeight'));
      return;
    }
    if (step === 7) {
      if (!formData.target_weight) {
        setError(t('wizard.targetWeight'));
        return;
      }
      // Convert to kg for comparison
      const startKg = weightUnit === 'lbs' ? convertWeightToKg(formData.start_weight) : formData.start_weight;
      const targetKg = weightUnit === 'lbs' ? convertWeightToKg(formData.target_weight) : formData.target_weight;
      if (targetKg >= startKg) {
        setError(t('newPlan.step2.subtitle'));
        return;
      }
    }
    
    if (step < TOTAL_STEPS) {
      setStep(step + 1);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    // Defensive guard: if the session token is gone (rare but possible on
    // iOS Safari when storage is wiped between tabs / a stale tab from
    // before login), bounce silently to login. No banner message — the
    // user hates seeing accusatory error copy when they just logged in.
    const earlyToken = localStorage.getItem('session_token');
    if (!earlyToken) {
      setSaving(false);
      navigate('/app/login');
      return;
    }

    try {
      // Update user name/nickname (non-blocking — don't fail plan creation for this)
      if (formData.display_name?.trim() || formData.nickname?.trim()) {
        try {
          const sessionToken = localStorage.getItem('session_token');
          const nameRes = await fetch(`${API_URL}/api/users/me`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              ...(sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {})
            },
            credentials: 'include',
            body: JSON.stringify({
              name: formData.display_name?.trim() || undefined,
              username: formData.nickname?.trim() || undefined
            })
          });
          if (!nameRes.ok) console.warn('Name update failed:', nameRes.status);
        } catch (nameErr) {
          console.warn('Name update error:', nameErr);
        }
      }

      // Convert weights to kg if needed
      const startWeightKg = weightUnit === 'lbs' ? convertWeightToKg(formData.start_weight) : formData.start_weight;
      const targetWeightKg = weightUnit === 'lbs' ? convertWeightToKg(formData.target_weight) : formData.target_weight;
      
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + formData.duration_weeks * 7);

      const planData = {
        name: formData.name,
        start_weight: startWeightKg,
        target_weight: targetWeightKg,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        // Include profile data for curve calculation
        height_cm: formData.height_cm,
        sex: formData.sex,
        age: formData.age,
        activity_level: formData.activity_level,
        weekly_goal: formData.weekly_goal
      };

      await createPlan(planData);
      
      // Log initial weight
      await logWeight({
        weight: startWeightKg,
        date: startDate.toISOString().split('T')[0]
      });

      // Note: NOT calling refreshUser() here. On iOS PWA, refreshUser
      // hits `/auth/me` with `withCredentials:true` which can re-pull
      // a STALE cookie session (different user than the one bearer-
      // authenticated above), stomping on `user` and making the just-
      // created plan look invisible on the next render. The Dashboard
      // page does its own `/api/dashboard` fetch on mount, so user
      // data refreshes naturally without this call.

      navigate('/app/dashboard');
    } catch (err) {
      const status = err?.response?.status || err?.status;
      const detail = err?.response?.data?.detail || err?.message || '';

      // Diagnostic logging — leave it on so production-only issues
      // (iOS PWA token races, prod-only validation errors) leave a
      // breadcrumb in the user's console for support.
      // eslint-disable-next-line no-console
      console.error('[NuovoPiano] handleSave failed', {
        status, detail,
        hasToken: !!localStorage.getItem('session_token'),
        url: err?.response?.url,
      });

      if (status === 401 || status === 403) {
        // Auth dropped mid-flow → silent redirect to login. Showing
        // a "Sessione scaduta" banner right after the user logged in
        // is confusing and feels like the app is broken.
        localStorage.removeItem('session_token');
        navigate('/app/login');
      } else {
        // Real server-side issue (validation, 500, network) — show
        // the actual reason so the user (and we) know what's wrong.
        setError(`Errore durante la creazione del piano${detail ? ': ' + detail : ''}`);
      }
    } finally {
      setSaving(false);
    }
  };

  // Calculate stats for summary
  const startWeightKg = weightUnit === 'lbs' ? convertWeightToKg(formData.start_weight) : formData.start_weight;
  const targetWeightKg = weightUnit === 'lbs' ? convertWeightToKg(formData.target_weight) : formData.target_weight;
  const weightLoss = startWeightKg && targetWeightKg ? (startWeightKg - targetWeightKg).toFixed(1) : 0;
  const weeklyLoss = weightLoss > 0 && formData.duration_weeks ? (weightLoss / formData.duration_weeks).toFixed(2) : 0;

  const activityOptions = [
    { value: 'sedentary', label: t('profile.sedentary'), desc: t('profile.sedentaryDesc'), factor: 1.2 },
    { value: 'light', label: t('profile.light'), desc: t('profile.lightDesc'), factor: 1.375 },
    { value: 'moderate', label: t('profile.moderate'), desc: t('profile.moderateDesc'), factor: 1.55 },
    { value: 'active', label: t('profile.active'), desc: t('profile.activeDesc'), factor: 1.725 },
    { value: 'very_active', label: t('profile.veryActive'), desc: t('profile.veryActiveDesc'), factor: 1.9 }
  ];

  const weeklyGoalOptions = [
    { value: 0.25, label: '0.25', desc: t('wizard.slow') },
    { value: 0.5, label: '0.5', desc: t('wizard.moderate') },
    { value: 0.75, label: '0.75', desc: t('wizard.fast') },
    { value: 1.0, label: '1.0', desc: t('wizard.aggressive') }
  ];

  return (
    <div className={`page-enter min-h-screen ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`} data-testid="nuovo-piano-page">
      {/* Fixed Header - with safe area */}
      <div className={`fixed-header ${isDark ? 'bg-[#080D1A]' : 'bg-white'}`}>
        <div className="px-4 py-3 flex items-center gap-4">
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
            className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isDark ? 'bg-white/5' : 'bg-gray-100'
            }`}
          >
            <ChevronLeft className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
          </button>
          <div className="flex-1">
            <h1 className={`font-display text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('newPlan.title')}</h1>
            <p className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{step}/{TOTAL_STEPS}</p>
          </div>
        </div>

        {/* Progress */}
        <div className="px-4 pb-2">
          <div className="flex gap-1">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map(i => (
              <div 
                key={i}
                className={`flex-1 h-1 rounded-full transition-colors ${
                  i <= step ? 'bg-[#E8334A]' : isDark ? 'bg-white/10' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content - fixed layout, no scroll, vertically centered */}
      <div 
        className="fixed inset-0 flex flex-col overflow-hidden"
        style={{ top: '100px', bottom: '90px' }}
        onTouchMove={(e) => e.preventDefault()}
      >
        <div className="flex-1 flex flex-col justify-center px-4">
        
        {/* Step 1: Name & Nickname */}
        {step === 1 && (
          <div className="space-y-5">
            <h2 className={`font-display text-2xl text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('wizard.whatsYourName') || 'Come ti chiami?'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className={`block text-sm mb-2 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {t('wizard.yourDisplayName') || 'Il tuo nome'}
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData({...formData, display_name: e.target.value})}
                  placeholder={t('wizard.namePlaceholder') || 'Es. Marco'}
                  className={`w-full p-4 rounded-xl text-lg border ${isDark ? 'bg-white/5 text-white border-white/10 placeholder-white/30' : 'bg-white text-gray-900 border-gray-200 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50`}
                  data-testid="wizard-display-name"
                  autoFocus
                />
              </div>
              <div>
                <label className={`block text-sm mb-2 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                  {t('wizard.yourNickname') || 'Nickname (visibile nel feed)'}
                </label>
                <input
                  type="text"
                  value={formData.nickname}
                  onChange={(e) => setFormData({...formData, nickname: e.target.value.replace(/\s/g, '')})}
                  placeholder={t('wizard.nicknamePlaceholder') || 'Es. marco_fit'}
                  className={`w-full p-4 rounded-xl text-lg border ${isDark ? 'bg-white/5 text-white border-white/10 placeholder-white/30' : 'bg-white text-gray-900 border-gray-200 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-[#E8334A]/50`}
                  data-testid="wizard-nickname"
                />
                <p className={`text-xs mt-1.5 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                  {t('wizard.nicknameHint') || 'Puoi cambiarlo in seguito dal profilo'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Height */}
        {step === 2 && (
          <div className="page-enter text-center">
            <h2 className={`font-display text-xl mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('wizard.yourHeight')}
            </h2>
            <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('wizard.personalData')}
            </p>
            
            <HeightPicker 
              value={formData.height_cm}
              onChange={(val) => setFormData({...formData, height_cm: val})}
              isDark={isDark}
              unit={heightUnit}
            />
            
            {error && <p className="text-[#E8334A] text-sm text-center mt-4">{error}</p>}
          </div>
        )}

        {/* Step 3: Sex - only Male/Female */}
        {step === 3 && (
          <div className="page-enter text-center">
            <h2 className={`font-display text-xl mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('wizard.yourSex')}
            </h2>
            <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('wizard.personalData')}
            </p>
            
            <div className="space-y-3">
              {[
                { value: 'male', label: t('profile.male') },
                { value: 'female', label: t('profile.female') }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setFormData({...formData, sex: option.value})}
                  className={`w-full p-4 rounded-xl text-left transition-all ${
                    formData.sex === option.value
                      ? option.value === 'female' ? 'bg-[#EC4899] text-white' : 'bg-[#2563EB] text-white'
                      : isDark ? 'bg-white/5 text-white/70 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="font-medium">{option.label}</span>
                </button>
              ))}
            </div>
            
            {error && <p className="text-[#E8334A] text-sm text-center mt-4">{error}</p>}
          </div>
        )}

        {/* Step 4: Age */}
        {step === 4 && (
          <div className="page-enter text-center">
            <h2 className={`font-display text-xl mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('wizard.yourAge') || 'Quanti anni hai?'}
            </h2>
            <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('wizard.ageForCurve') || 'Serve per calcolare la tua curva'}
            </p>
            
            <AgePicker 
              value={formData.age} 
              onChange={(age) => setFormData({...formData, age})} 
              isDark={isDark} 
            />
            
            {error && <p className="text-[#E8334A] text-sm text-center mt-4">{error}</p>}
          </div>
        )}

        {/* Step 5: Activity Level - compact */}
        {step === 5 && (
          <div className="page-enter text-center">
            <h2 className={`font-display text-lg mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('wizard.yourActivity')}
            </h2>
            <p className={`text-sm mb-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('wizard.chooseActivity')}
            </p>
            
            <div className="space-y-1.5">
              {activityOptions.map(option => (
                <button
                  key={option.value}
                  onClick={() => setFormData({...formData, activity_level: option.value})}
                  className={`w-full p-2.5 rounded-xl text-left transition-all ${
                    formData.activity_level === option.value
                      ? 'bg-[#16A34A] text-white'
                      : isDark ? 'bg-white/5 text-white/70 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="font-medium block text-sm">{option.label}</span>
                  <span className={`text-xs ${formData.activity_level === option.value ? 'text-white/80' : isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    {option.desc}
                  </span>
                </button>
              ))}
            </div>
            
            {error && <p className="text-[#E8334A] text-sm text-center mt-3">{error}</p>}
          </div>
        )}

        {/* Step 6: Current Weight */}
        {step === 6 && (
          <div className="page-enter text-center">
            <h2 className={`font-display text-xl mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('wizard.currentWeight')}
            </h2>
            <p className={`text-sm mb-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('newPlan.step1.subtitle')}
            </p>
            
            <WeightDrumPicker 
              value={formData.start_weight}
              onChange={(val) => {
                // Auto-update target weight to be 10kg/22lbs less
                const targetDiff = weightUnit === 'lbs' ? 22 : 10;
                setFormData({
                  ...formData, 
                  start_weight: val,
                  target_weight: Math.max(val - targetDiff, weightUnit === 'lbs' ? 88 : 40)
                });
              }}
              isDark={isDark}
              unit={weightUnit}
            />
            
            {error && <p className="text-[#E8334A] text-sm text-center mt-4">{error}</p>}
          </div>
        )}

        {/* Step 7: Target Weight + Weekly Goal - compact */}
        {step === 7 && (
          <div className="page-enter text-center">
            <h2 className={`font-display text-lg mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('wizard.targetWeight')}
            </h2>
            <p className={`text-sm mb-2 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('newPlan.step2.subtitle')}
            </p>
            
            <WeightDrumPicker 
              value={formData.target_weight}
              onChange={(val) => setFormData({...formData, target_weight: val})}
              isDark={isDark}
              unit={weightUnit}
            />
            
            {weightLoss > 0 && (
              <div className="glass-card-sm p-2 text-center mt-2">
                <p className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-500'}`}>{t('wizard.toLose')}</p>
                <p className="font-display text-lg text-[#16A34A]">
                  -{weightUnit === 'lbs' ? (weightLoss * 2.20462).toFixed(1) : weightLoss} {weightUnit}
                </p>
              </div>
            )}
            
            {/* Weekly Goal - more compact */}
            <div className="mt-3">
              <h3 className={`text-xs font-medium mb-1.5 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                {t('wizard.weeklyGoal')}
              </h3>
              <div className="grid grid-cols-4 gap-1.5">
                {weeklyGoalOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => setFormData({...formData, weekly_goal: option.value})}
                    className={`p-1.5 rounded-lg text-center transition-all ${
                      formData.weekly_goal === option.value
                        ? 'bg-[#E8334A] text-white'
                        : isDark ? 'bg-white/5 text-white/70' : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    <span className="font-display text-sm block">{option.label}</span>
                    <span className="text-[10px] opacity-70">{weightUnit}/{t('wizard.weeks').slice(0, 1)}</span>
                  </button>
                ))}
              </div>
            </div>
            
            {error && <p className="text-[#E8334A] text-sm text-center mt-3">{error}</p>}
          </div>
        )}

        {/* Step 8: Duration + Summary */}
        {step === 8 && (
          <div className="page-enter text-center">
            <h2 className={`font-display text-xl mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('wizard.planDuration')}
            </h2>
            <p className={`text-sm mb-3 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
              {t('wizard.chooseDuration')}
            </p>
            
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[8, 12, 16, 20, 24, 32].map(weeks => {
                const endDate = new Date();
                endDate.setDate(endDate.getDate() + weeks * 7);
                const dateStr = endDate.toLocaleDateString(language === 'en' ? 'en-US' : language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : 'fr-FR', { day: 'numeric', month: 'short' });
                
                return (
                  <button
                    key={weeks}
                    onClick={() => setFormData({...formData, duration_weeks: weeks})}
                    className={`p-2.5 rounded-xl text-center transition-all ${
                      formData.duration_weeks === weeks
                        ? 'bg-[#E8334A] text-white'
                        : isDark ? 'bg-white/5 text-white/70 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span className="font-display text-lg block">{weeks}</span>
                    <span className="text-xs opacity-70 block">{t('wizard.weeks')}</span>
                    <span className={`text-xs block ${formData.duration_weeks === weeks ? 'text-white/80' : isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      {dateStr}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Summary */}
            <div className={`rounded-xl p-3 text-left ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
              <h3 className={`font-display text-sm mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('wizard.summary')}</h3>
              
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className={isDark ? 'text-white/60' : 'text-gray-500'}>{t('wizard.startWeight')}</span>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {formData.start_weight.toFixed(1)} {weightUnit}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-white/60' : 'text-gray-500'}>{t('wizard.goalWeight')}</span>
                  <span className="font-medium text-[#16A34A]">
                    {formData.target_weight.toFixed(1)} {weightUnit}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={isDark ? 'text-white/60' : 'text-gray-500'}>{t('wizard.toLose')}</span>
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    -{weightUnit === 'lbs' ? (weightLoss * 2.20462).toFixed(1) : weightLoss} {weightUnit}
                  </span>
                </div>
                <div className={`border-t pt-1.5 flex justify-between ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                  <span className={isDark ? 'text-white/60' : 'text-gray-500'}>{t('wizard.weeklyAverage')}</span>
                  <span className="font-medium text-[#F59E0B]">
                    -{weightUnit === 'lbs' ? (weeklyLoss * 2.20462).toFixed(2) : weeklyLoss} {weightUnit}/{t('wizard.weeks').slice(0, 1)}
                  </span>
                </div>
              </div>
            </div>

            {error && <p className="text-[#E8334A] text-sm text-center mt-2">{error}</p>}
          </div>
        )}
        
        </div>
      </div>

      {/* Bottom Action - fixed at bottom */}
      <div 
        className={`fixed left-0 right-0 p-4 bg-gradient-to-t ${
          isDark ? 'from-[#080D1A] via-[#080D1A]' : 'from-white via-white'
        } to-transparent`}
        style={{ bottom: '0', paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="max-w-md mx-auto">
          {step < TOTAL_STEPS ? (
            <button 
              onClick={handleNext}
              className="btn-primary w-full py-4 flex items-center justify-center gap-2"
              data-testid="next-step-btn"
            >
              {t('newPlan.continue')} <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button 
              onClick={handleSave}
              disabled={saving}
              className="btn-primary w-full py-4"
              data-testid="create-plan-btn"
            >
              {saving ? t('common.loading') : t('newPlan.startPlan')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
